import {
    useMemo, useState
} from 'react';
import {
    Bug, Copy, Trash2, ArrowRight
} from 'lucide-react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from 'shared/components/ui/dialog';

function pretty(value) {
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

/** Merge consecutive stream tokens into one event so the pane can wrap. */
function wrapTextEvents(events) {
    const out = [];
    for (const evt of events) {
        const last = out[out.length - 1];
        if (evt.type === 'text' && last?.type === 'text') {
            last.text += evt.delta || evt.text || '';
            last.chunks += 1;
            last.timeEnd = evt.time;
            continue;
        }
        if (evt.type === 'text') {
            out.push({
                id: evt.id,
                time: evt.time,
                timeEnd: evt.time,
                type: 'text',
                chunks: 1,
                text: evt.delta || evt.text || '',
            });
            continue;
        }
        out.push(evt);
    }
    return out;
}

function debugLabel(evt) {
    if (evt.type === 'turn-start') return 'user asked BEXI';
    if (evt.type === 'request') return `${evt.method} ${evt.url}`;
    if (evt.type === 'response') return `HTTP ${evt.status}`;
    if (evt.type === 'text') {
        return evt.chunks > 1 ? `assistant text (${evt.chunks} tokens)` : 'assistant text';
    }
    if (evt.type === 'tool-input-start') return `preparing ${evt.toolName}`;
    if (evt.type === 'tool-call' && evt.toolName === 'bexi_set_checklist') return 'set checklist';
    if (evt.type === 'tool-call' && evt.toolName === 'bexi_update_checklist_step') return 'update checklist step';
    if (evt.type === 'tool-call' && evt.toolName === 'bexi_finish') return 'finish task';
    if (evt.type === 'tool-call') return `call ${evt.toolName}`;
    if (evt.type === 'tool-result') return `result ${evt.toolName}`;
    if (evt.type === 'done') return `done ${evt.finishReason || ''}`.trim();
    if (evt.type === 'question-answer') return `answered ${evt.questionId || 'question'}`;
    if (evt.type === 'error') return `error ${evt.error || ''}`.trim();
    return evt.type || 'event';
}

function debugMeaning(evt) {
    if (!evt) return 'No event selected yet.';
    if (evt.type === 'turn-start') {
        return 'A new BEXI turn started in the browser. This is the message and options before anything was sent to the backend.';
    }
    if (evt.type === 'request') {
        return 'The browser sent this request to bex-server. This is where the user message, model, session id, page context, and auto-run setting go.';
    }
    if (evt.type === 'response') {
        return evt.ok
            ? 'bex-server accepted the request and opened the live stream back to the browser.'
            : 'bex-server rejected the request before BEXI could run.';
    }
    if (evt.type === 'text') {
        return 'The model sent words back. Many tiny text tokens are grouped here so the log is readable.';
    }
    if (evt.type === 'tool-input-start') {
        return 'BEXI decided it needs a tool and is preparing the tool request.';
    }
    if (evt.type === 'tool-call') {
        if (evt.toolName === 'bexi_set_checklist') {
            return 'BEXI created the visible checklist for this task.';
        }
        if (evt.toolName === 'bexi_update_checklist_step') {
            return 'BEXI changed one checklist step, usually to running, done, or failed.';
        }
        if (evt.toolName === 'bexi_finish') {
            return 'BEXI declared the task finished or blocked. This stops auto-continue.';
        }
        return evt.gated
            ? 'BEXI wants to run a write tool, but auto-run is off, so this becomes an approval card instead of executing now.'
            : 'BEXI called this tool. The input is the request body sent into that tool.';
    }
    if (evt.type === 'tool-result') {
        return 'The tool finished. The result is the data BEXI receives and uses for its next step.';
    }
    if (evt.type === 'done') {
        return 'This BEXI turn ended. If there are pending writes, BEXI is waiting for human approval.';
    }
    if (evt.type === 'question-answer') {
        return 'The user answered a structured BEXI question. The answer is sent back as the next chat turn.';
    }
    if (evt.type === 'approved-write-result') {
        return 'You approved a write action, then the browser called the backend to execute that tool.';
    }
    if (evt.type === 'error') return 'Something failed during the BEXI turn.';
    return 'A stream event from the BEXI loop.';
}

function eventData(evt) {
    if (!evt) return {};
    if (evt.type === 'turn-start') {
        return {
            userText: evt.userText,
            model: evt.model,
            hidden: evt.hidden,
            sessionId: evt.sessionId,
            messageCount: evt.messageCount,
        };
    }
    if (evt.type === 'request') return evt.body || evt;
    if (evt.type === 'tool-call') {
        return {
            tool: evt.toolName,
            isWrite: evt.isWrite,
            gated: evt.gated,
            input: evt.input,
        };
    }
    if (evt.type === 'tool-result') {
        return {
            tool: evt.toolName,
            result: evt.result,
        };
    }
    if (evt.type === 'text') {
        return {
            text: evt.text,
            chunks: evt.chunks,
        };
    }
    return evt;
}

export default function BexiDebugDialog({
    open, onOpenChange, events, onClear
}) {
    const wrapped = wrapTextEvents(events);
    const [selected, setSelected] = useState(0);
    const safeSelected = Math.min(selected, Math.max(wrapped.length - 1, 0));
    const selectedEvent = wrapped[safeSelected];
    const body = pretty(wrapped);
    const selectedBody = useMemo(() => pretty(eventData(selectedEvent)), [selectedEvent]);
    const copy = () => navigator.clipboard?.writeText(body);
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="z-[90] w-[95vw] max-w-5xl gap-3 p-0">
                <DialogHeader className="border-b px-5 pb-3 pt-5 pr-12">
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <Bug className="h-4 w-4" /> BEXI Debug Trace
                    </DialogTitle>
                    <DialogDescription>
                        Live request, stream, question, tool call, tool result, and completion events for this chat.
                        Consecutive assistant tokens are merged into one wrapped text event.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex min-h-0 flex-1 flex-col px-5 pb-5">
                    <div className="mb-3 rounded-md border bg-muted/40 p-3 text-sm">
                        <div className="font-medium">Read it like this</div>
                        <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                            User asks <ArrowRight className="h-3 w-3" /> browser sends request
                            <ArrowRight className="h-3 w-3" /> server streams events
                            <ArrowRight className="h-3 w-3" /> BEXI calls tools
                            <ArrowRight className="h-3 w-3" /> tools return data
                            <ArrowRight className="h-3 w-3" /> BEXI answers, asks, or waits for approval
                        </div>
                    </div>
                    <div className="mb-3 flex items-center justify-between gap-2">
                        <div className="text-xs text-muted-foreground">
                            {wrapped.length} events
                            {events.length !== wrapped.length
                                ? ` (${events.length - wrapped.length} tokens folded)`
                                : ''}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={copy} className="gap-1">
                                <Copy className="h-3.5 w-3.5" /> Copy
                            </Button>
                            <Button variant="outline" size="sm" onClick={onClear} className="gap-1" disabled={!events.length}>
                                <Trash2 className="h-3.5 w-3.5" /> Clear
                            </Button>
                        </div>
                    </div>
                    <div className="grid min-h-0 gap-3 md:grid-cols-[260px_1fr]">
                        <div className="max-h-[60vh] overflow-y-auto rounded-md border">
                            {wrapped.length === 0 ? (
                                <div className="p-3 text-xs text-muted-foreground">No events yet.</div>
                            ) : wrapped.map((evt, i) => (
                                <button
                                    key={evt.id || i}
                                    type="button"
                                    onClick={() => setSelected(i)}
                                    className={`block w-full border-b px-3 py-2 text-left last:border-b-0 ${i === safeSelected ? 'bg-muted' : 'hover:bg-muted/60'}`}
                                >
                                    <div className="truncate text-xs font-medium">{debugLabel(evt)}</div>
                                    <div className="text-[10.5px] text-muted-foreground">{evt.time}</div>
                                </button>
                            ))}
                        </div>
                        <div className="max-h-[60vh] overflow-auto rounded-md border">
                            <div className="border-b bg-background p-3">
                                <div className="text-sm font-semibold">{selectedEvent ? debugLabel(selectedEvent) : 'No event'}</div>
                                <p className="mt-1 text-xs leading-5 text-muted-foreground">{debugMeaning(selectedEvent)}</p>
                            </div>
                            <div className="p-3">
                                <div className="mb-2 text-xs font-medium">Data for this step</div>
                                <pre className="overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 text-[11px] leading-5">
                                    {selectedBody}
                                </pre>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
